import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type InquiryMessageRow, type InquiryRow } from '../../lib/api.js';

interface DetailResponse {
  inquiry: InquiryRow;
  messages: InquiryMessageRow[];
}

export function CustomerInquiryDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  async function load() {
    if (!id) return;
    try {
      const res = await api.get<DetailResponse>(`/api/customer/inquiries/${id}`);
      setData(res);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/api/customer/inquiries/${id}/messages`, { body: reply });
      setReply('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function close() {
    if (!id) return;
    if (!confirm('이 문의를 종료하시겠습니까?')) return;
    try {
      await api.post(`/api/customer/inquiries/${id}/close`, {});
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!data) {
    return (
      <div>
        {error ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        ) : (
          <div className="text-neutral-500">로딩…</div>
        )}
      </div>
    );
  }

  const inq = data.inquiry;
  const isOpen = inq.status === 'open';

  return (
    <div>
      <Link to="/customer/inquiries" className="text-xs text-neutral-500 hover:text-neutral-800">
        ← 목록
      </Link>
      <header className="mt-2 mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{inq.subject}</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {inq.category === 'product' ? '제품 문의' : '라이선스 문의'} ·{' '}
            {new Date(inq.createdAt).toLocaleString('ko-KR')}
          </p>
        </div>
        <div>
          {isOpen ? (
            <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
              open
            </span>
          ) : (
            <span className="inline-block px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs font-medium rounded">
              closed
            </span>
          )}
        </div>
      </header>

      <div className="space-y-3 mb-6">
        {data.messages.map((m) => (
          <div
            key={m.id}
            className={`p-4 rounded-lg border ${
              m.authorType === 'admin'
                ? 'bg-brand-50 border-brand-200'
                : 'bg-surface border-neutral-200'
            }`}
          >
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm font-medium">
                {m.authorName}
                <span className="ml-2 text-xs text-neutral-500">
                  {m.authorType === 'admin' ? '관리자' : '고객사'}
                </span>
              </span>
              <span className="text-xs text-neutral-500">
                {new Date(m.createdAt).toLocaleString('ko-KR')}
              </span>
            </div>
            <div className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">
              {m.body}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {isOpen ? (
        <form onSubmit={send} className="bg-surface border border-neutral-200 rounded-lg p-4 space-y-3">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            required
            rows={5}
            placeholder="답글 작성…"
            className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="px-4 py-2 bg-brand-400 hover:bg-brand-600 disabled:bg-neutral-300 text-white text-sm font-medium rounded-md"
            >
              {sending ? '전송 중…' : '답글'}
            </button>
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 border border-neutral-300 text-neutral-700 text-sm rounded-md hover:bg-neutral-50"
            >
              문의 종료
            </button>
          </div>
        </form>
      ) : (
        <div className="p-4 bg-neutral-100 rounded-md text-sm text-neutral-600">
          이 문의는 종료되었습니다.
        </div>
      )}
    </div>
  );
}
